<template>
  <div class="mx-3 mx-md-0">
    <section-title has-line content="Consulta de Estudiantes" />
    <div v-if="!isReadOnly" class="d-flex align-items-center">
      <em class="far fa-exclamation-circle color-secondary-6 mr-1 font-14" />
      <span class="color-secondary-6 font-14">Esta consulta es opcional.</span>
    </div>
    <div v-if="!isReadOnly" class="row">
      <div class="col col-sm col-12">
        <validation-observer ref="searchStudentRef">
          <div class="row mt-3">
            <div
              class="col-md-6 col-sm-12 mt-3 mt-md-0"
              :class="{
                'col-md-12': showStudentDetailsInfo,
              }"
            >
              <validation-provider
                v-slot="{ errors }"
                :custom-messages="{
                  required: 'El número SIE o número de consulta es requerido.',
                  startsLikeTapRequestNumber:
                    'El número de consulta no es válido.',
                  studentSie: 'El número de SIE no es válido.',
                }"
                :rules="{
                  required: true,
                  startsLikeTapRequestNumber: startWithLetters,
                  studentSie: !startWithLetters,
                }"
                mode="eager"
              >
                <custom-input
                  v-model="studentSieIdOrRequestNumber"
                  :error="errors[0]"
                  icon="fa fa-search orange-icon"
                  label-class="font-14"
                  left-icon
                  placeholder="Escriba el SIE o Número de Consulta"
                />
              </validation-provider>
            </div>
            <div
              v-if="!showStudentDetailsInfo"
              class="col-12 col-md-6 px-0 text-right text-md-left mt-2 mt-md-0 pr-3 pr-md-0"
            >
              <action-button
                i="fa fa-search"
                text="Buscar"
                variant="success px-3"
                @click="handlerSearch"
              />
            </div>
          </div>
        </validation-observer>
      </div>
      <div class="col-7 col-sm col-12 mt-3">
        <div v-if="showStudentDetailsInfo" class="border-left px-4">
          <span
            class="text-center font-16 bg-gray-2 px-4 py-2 font-weight-bold"
            style="border-radius: 20px; color: #000"
          >
            Resultado
          </span>
          <div class="d-flex align-items-center mb-2 mt-3">
            <span class="color-gray-6 font-14 mr-2">Nombre:</span>
            <span class="color-black font-16">
              {{ studentInfo?.studentFullName }}
            </span>
          </div>
          <div class="d-flex align-items-center mb-2">
            <span class="color-gray-6 font-14 mr-2">SIE:</span>
            <span class="color-black font-16">
              {{ studentInfo?.studentSieId }}
            </span>
          </div>
          <div class="d-flex align-items-center mb-2">
            <span class="color-gray-6 font-14 mr-2">No. de Consulta:</span>
            <span class="color-black font-16">
              {{ studentInfo?.requestNumber }}
            </span>
          </div>
          <div class="d-flex align-items-center mb-2">
            <span class="color-gray-6 font-14 mr-2">Estatus:</span>
            <span class="color-black font-16">
              {{ studentInfo?.requestStatusName }}
            </span>
          </div>
          <div class="d-flex align-items-center mb-2">
            <span class="color-gray-6 font-14 mr-2">Escuela:</span>
            <span class="color-black font-16">
              {{ studentInfo?.locationSchoolName }}
            </span>
          </div>
          <div class="student-info-action">
            <action-button
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger px-4 mr-3"
              @click="handleCancel"
            />
            <action-button
              i="fa fa-chevron-circle-right"
              text="Añadir"
              variant="success px-4"
              @click="handleAddStudent"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasStudents">
      <div class="color-secondary-6 font-14 mt-4 pt-2 mb-3">
        Estudiante(s) Seleccionado(s):
      </div>
      <div
        class="row bg-secondary-28 border-color--gray-5  py-3 mx-0 mb-2 rounded"
        v-for="student in paginatedStudentList"
        :key="`student-${student.studentSieId}`"
      >
        <div class="col col-sm col-12">
          <div class="color-gray-6 font-14 mr-2">Nombre:</div>
          <div class="color-black font-16">
            {{ student.studentFullName }}
          </div>
        </div>
        <div class="col col-sm col-12 mt-2 mt-md-0">
          <div class="color-gray-6 font-14 mr-2">SIE:</div>
          <div class="color-black font-16">
            {{ student.studentSieId }}
          </div>
        </div>
        <div class="col col-sm col-12 mt-2 mt-md-0">
          <div class="color-gray-6 font-14 mr-2">No. de Consulta:</div>
          <div class="color-black font-16">
            {{ student.requestNumber }}
          </div>
        </div>
        <div class="col col-sm col-12 mt-2 mt-md-0">
          <div class="color-gray-6 font-14 mr-2">Estatus:</div>
          <div class="color-black font-16">
            {{ student.requestStatusName }}
          </div>
        </div>
        <div class="col col-sm col-12 mt-2 mt-md-0">
          <div class="color-gray-6 font-14 mr-2">Escuela:</div>
          <div class="color-black font-16">
            {{ student.locationSchoolName }}
          </div>
        </div>
        <div
          v-if="!isReadOnly"
          class="col col-sm col-12 mt-2 mt-md-0 d-flex align-items-center justify-content-end"
        >
          <action-button
            i="fas fa-minus-circle"
            text="Remover"
            variant="outline-danger px-4 mr-3"
            @click="handleRemoveStudent(student)"
          />
        </div>
      </div>
      <pagination
        v-if="pageCount > 1"
        :data="paginationData"
        @pagination-go-page="currentPage = $event"
      />
    </div>
    <custom-result-not-found
      v-else-if="isReadOnly"
      class="mt-4"
      text="No hay estudiantes para mostrar."
    />
  </div>
</template>

<script>
// third party libs
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';

// libs
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';

// mixins
import alertMixin from '@/mixin/alert.mixin';

// services
import * as api from '@/views/pei/location-alternatives/_api';

extend('startsLikeTapRequestNumber', {
  validate: (value) => {
    if (!value || value == undefined) return false;
    let regex = /^([aA-zZ]{2,3})-([\d]{4})-([\d]{2})-([\d]{6})/g;

    return value.search(regex) !== -1;
  },
});

export default {
  name: 'SearchStudent',
  mixins: [alertMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomInput,
    ActionButton,
    Pagination,
    CustomResultNotFound,
  },
  props: {
    actionPlanId: {
      type: Number,
      default: null,
    },
    studentData: {
      type: Array,
      default: () => [],
    },
    academicYearId: {
      type: Number | undefined,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    currentPage: 1,
    itemsPerPage: 5,
    studentSieIdOrRequestNumber: undefined,
    studentInfo: {},
    studentSelectedList: [],
    actionPlanIdValue: null,
  }),
  watch: {
    studentData: {
      immediate: true,
      handler(newVal) {
        this.studentSelectedList = newVal ?? [];
        this.updatePagination();
      },
    },
    actionPlanId: {
      immediate: true,
      handler(newVal) {
        this.actionPlanIdValue = newVal;
      },
    },
  },
  computed: {
    startWithLetters() {
      const regex = /^[a-zA-Z].*/;
      return this.studentSieIdOrRequestNumber
        ? this.studentSieIdOrRequestNumber.match(regex)?.length > 0
        : false;
    },
    showStudentDetailsInfo() {
      return Object.values(this.studentInfo).length;
    },
    hasStudents() {
      return this.studentSelectedList.length;
    },
    pageCount() {
      return Math.ceil(this.studentSelectedList.length / this.itemsPerPage);
    },
    paginatedStudentList() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.studentSelectedList.slice(startIndex, endIndex);
    },
    paginationData() {
      return {
        currentPage: this.currentPage,
        totalItemCount: this.studentSelectedList.length,
        pageCount: this.pageCount,
        count: this.itemsPerPage,
      };
    },
  },
  methods: {
    async handlerSearch() {
      const isValid = await this.$refs.searchStudentRef.validate();
      if (!isValid) return;

      const response = await api
        .searchAlternativeLocationActionPlanStudents(
          this.studentSieIdOrRequestNumber,
          this.academicYearId
        )
        .catch((err) => {
          this.ShowToast('Error', err.response.data.message, 'error');
        });

      if (response?.data) {
        this.studentInfo = response.data;
      }
    },
    async handleAddStudent() {
      const isStudentInList = this.studentSelectedList.some(
        (student) => student.studentSieId === this.studentInfo.studentSieId
      );
      if (isStudentInList) {
        this.ShowToast(
          'Error',
          'El estudiante ya ha sido seleccionado.',
          'error'
        );
        return;
      }
      if (this.actionPlanIdValue) {
        const response = await api
          .saveAlternativeLocationActionPlanStudent(
            this.actionPlanIdValue,
            this.studentInfo
          )
          .catch((error) => {
            this.ShowToast('Error', error.message, 'error');
          });
        if (!response?.data) {
          return;
        }
      }

      this.studentSelectedList.push({ ...this.studentInfo });
      this.updatePagination();
      this.handleCancel();
    },
    async handleRemoveStudent(student) {
      if (this.actionPlanIdValue) {
        let alertResponse = await this.ShowCancelModal(
          'Atención',
          `¿Esta seguro que desea eliminar el estudiante"?`,
          'Sí',
          'No'
        );
        if (!alertResponse) return;
        const response = await api
          .removeAlternativeLocationActionPlanStudent(
            this.actionPlanIdValue,
            student.studentSieId
          )
          .catch((error) => {
            this.ShowToast('Error', error.message, 'error');
          });
        if (!response?.data) {
          return;
        }
      }
      this.studentSelectedList = this.studentSelectedList.filter(
        (s) => s.studentSieId !== student.studentSieId
      );
      this.updatePagination();
    },
    handleCancel() {
      this.studentSieIdOrRequestNumber = undefined;
      this.studentInfo = {};
      this.$refs.searchStudentRef.reset();
    },
    updatePagination() {
      const newPageCount = Math.ceil(
        this.studentSelectedList.length / this.itemsPerPage
      );
      if (this.currentPage > newPageCount && newPageCount > 0) {
        this.currentPage = newPageCount;
      }
      if (this.studentSelectedList.length === 0) {
        this.currentPage = 1;
      }
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    getModel() {
      return [...this.studentSelectedList];
    },
  },
};
</script>

<style scoped lang="scss">
.student-info-action {
  position: absolute;
  right: 26px;
  margin-top: -36px;
}

@media only screen and (max-width: 768px) {
  .student-info-action {
    position: relative;
    right: 0;
    margin-top: 10px;
  }
}
</style>
